import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder } from '@angular/forms';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

@Component({
  templateUrl: 'noise-detail.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class NoiseDetailPage implements OnInit {

  loaded = false;
  isRightSideContent = true;
  dialogParams: any;
  propertyName = '';
  propertyId: number;

  chartSeries: any;
  chartOptions: any;
  chartXaxis: any;
  chartYaxis: any;
  chartStroke: any;
  chartGrid: any;
  chartTooltip: any;

  anomalies: any[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private navCtrl: CustomNavController,
    public windowService: WindowService,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private cdr: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.dialogParams = await this.storage.retrieve('dialog-params');
    }
    this.propertyName = this.dialogParams?.propertyName || 'Property';
    this.propertyId = this.dialogParams?.propertyId;
    this.rightSidePanelService.setDialogPageTitle(this.propertyName);
    this.generateChartData();
    this.generateAnomalies();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  generateChartData() {
    const dates = [];
    const data = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      dates.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      const base = 45;
      data.push(Math.round(base + (Math.random() * 30 - 10)));
    }

    this.chartSeries = [{ name: this.propertyName, data }];
    this.chartOptions = { type: 'line', height: 200, toolbar: { show: false } };
    this.chartXaxis = {
      categories: dates,
      labels: { style: { colors: '#999', fontSize: '11px' }, rotate: -45, rotateAlways: true },
      tickAmount: 10
    };
    this.chartYaxis = {
      title: { text: 'dB' },
      labels: { style: { colors: '#999', fontSize: '12px' }, formatter: (val) => Math.round(val).toString() }
    };
    this.chartStroke = { curve: 'smooth', width: 2, colors: ['#00aaba'] };
    this.chartGrid = { borderColor: '#333', strokeDashArray: 3 };
    this.chartTooltip = { enabled: true, theme: 'dark', y: { formatter: (val) => `${val} dB` } };
  }

  generateAnomalies() {
    const statusColors = { alert: '#C62828', warning: '#F9A825', normal: '#2E7D32' };
    const now = new Date();
    this.anomalies = [
      {
        id: 1,
        date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 0, 23, 14),
        noiseLevel: 82,
        duration: '47 min',
        status: 'alert',
        statusLabel: 'ALERT',
        statusColor: statusColors['alert'],
        notes: [
          { id: 1, text: 'Called guest — party winding down, agreed to keep it quiet.', createdBy: 'You', createdAt: '2026-02-20T23:45:00Z' },
        ],
        noteForm: this.fb.group({ note: [''] }),
      },
      {
        id: 2,
        date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 21, 30),
        noiseLevel: 74,
        duration: '22 min',
        status: 'alert',
        statusLabel: 'ALERT',
        statusColor: statusColors['alert'],
        notes: [],
        noteForm: this.fb.group({ note: [''] }),
      },
      {
        id: 3,
        date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 2, 18, 5),
        noiseLevel: 61,
        duration: '12 min',
        status: 'warning',
        statusLabel: 'WARNING',
        statusColor: statusColors['warning'],
        notes: [
          { id: 1, text: 'Seems like a one-off — guest was watching a movie. No action needed.', createdBy: 'You', createdAt: '2026-02-18T19:00:00Z' },
        ],
        noteForm: this.fb.group({ note: [''] }),
      },
      {
        id: 4,
        date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 4, 1, 20),
        noiseLevel: 78,
        duration: '35 min',
        status: 'alert',
        statusLabel: 'ALERT',
        statusColor: statusColors['alert'],
        notes: [
          { id: 1, text: 'Sent automated message via noise agent. Guest responded positively.', createdBy: 'Noise Agent', createdAt: '2026-02-16T01:25:00Z' },
          { id: 2, text: 'Noise dropped to normal within 10 min of message.', createdBy: 'You', createdAt: '2026-02-16T01:40:00Z' },
        ],
        noteForm: this.fb.group({ note: [''] }),
      },
      {
        id: 5,
        date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6, 22, 45),
        noiseLevel: 58,
        duration: '8 min',
        status: 'warning',
        statusLabel: 'WARNING',
        statusColor: statusColors['warning'],
        notes: [],
        noteForm: this.fb.group({ note: [''] }),
      },
      {
        id: 6,
        date: new Date(now.getFullYear(), now.getMonth(), now.getDate() - 9, 0, 10),
        noiseLevel: 85,
        duration: '52 min',
        status: 'alert',
        statusLabel: 'ALERT',
        statusColor: statusColors['alert'],
        notes: [
          { id: 1, text: 'Guest checked out next day — added to watch list for future bookings.', createdBy: 'You', createdAt: '2026-02-11T09:00:00Z' },
        ],
        noteForm: this.fb.group({ note: [''] }),
      },
    ];

    for (const a of this.anomalies) {
      a.dateFormatted = a.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      a.timeFormatted = a.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
  }

  addNote(anomaly: any) {
    const text = anomaly.noteForm.get('note')?.value?.trim();
    if (!text) {return;}
    anomaly.notes.push({
      id: anomaly.notes.length + 1,
      text,
      createdBy: 'You',
      createdAt: new Date().toISOString(),
    });
    anomaly.noteForm.get('note')?.setValue('');
    this.cdr.markForCheck();
  }
}
